import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import styled from 'styled-components';

import { ERROR_MESSAGES } from 'core/api/constants';
import { SIGN_UP } from 'core/api/apollo/mutations';
import cacheUpdater, { updateUserQuery } from 'core/api/cache/updaters';
import Tracker from 'core/api/tracking/tracker';
import withForm from 'core/ui/components/withForm';
import handlesErrors from 'core/ui/components/HandlesErrors';

import { onrampNext } from 'onramp/api/navigation';
import { FormGroup, Label, ValidatedInput } from 'core/ui/atoms';
import { ContentButton, ContentForm, InfoHeader, InfoImage, Page, PageContent, PageInfo } from 'onramp/ui/components';
import PageImage from 'onramp/assets/images/SignUp/main.png';

// NOTE: update the current user query so the auth checks work
@graphql(SIGN_UP, {
  options: {
    update: (data, data1) => {
      cacheUpdater('signUp', updateUserQuery)(data,data1);
},
  },
})
@handlesErrors
class SignUpPage extends PureComponent {
  static propTypes = {
    currentUser: PropTypes.object,
    cache: PropTypes.object.isRequired,
    mutate: PropTypes.func.isRequired,
  };

  onSuccess = () => {
    Tracker.trackRegistration();
    onrampNext(this.props.currentUser, this.props.cache);
  };

  componentDidMount() {
    Tracker.trackLead();
  }

  render() {
    const { mutate } = this.props;

    return (
      <Page backgroundColor="#A4B4AE">
        <CustomPageInfo>
          <CustomInfoHeader text-color='inverse'>Let's see if we deliver to you</CustomInfoHeader>
          <CustomInfoImage
            src={PageImage}
            title="Miso Ginger Soba Bowl + Shiitake Chicken"
            subtitle="Ginger Miso Carrot Puree, Hijiki Salad, Pickled Burdock, Edamame, Watermelon Radish, Black Rice Soba Noodle"
          />
        </CustomPageInfo>
        <CustomPageContent>
          <StepForm
            onSubmit={mutate}
            onSuccess={this.onSuccess}
          />
        </CustomPageContent>
      </Page>
    );
  }
}

@withForm('signUp')
class StepForm extends PureComponent {
  static propTypes = {
    form: PropTypes.object.isRequired,
  };

  isValid = () => {
    const { form: { formData: { firstName, lastName, zipCode, email } } } = this.props;
    return firstName && lastName && zipCode && email;
  };

  onSubmit = (e) => {
    e.preventDefault();
    const { form: { handleSubmit } } = this.props;
    handleSubmit(this.isValid);
  };

  render() {
    const { form: { formData, onChange, isProcessing } } = this.props;

    return (
      <ContentForm
        onSubmit={this.onSubmit}
        noValidate
        instantValidate={false}
      >
        <FormGroup>
          <Label text-color='inverse' required>Zip Code</Label>
          <ValidatedInput
            inverse
            type="text"
            name="zipCode"
            placeholder="Type zip code"
            maxLength={5}
            value={formData.zipCode}
            onChange={onChange}
            validators={['required', 'isNumber', 'minStringLength:5']}
            errorMessages={[
              ERROR_MESSAGES.required,
              ERROR_MESSAGES.numeric,
              ERROR_MESSAGES.zip,
            ]}
          />
        </FormGroup>
        <FormGroup>
          <Label text-color='inverse' required>First Name</Label>
          <ValidatedInput
            inverse
            type="text"
            name="firstName"
            placeholder="Type first name"
            value={formData.firstName}
            onChange={onChange}
            validators={['required']}
            errorMessages={[ERROR_MESSAGES.required]}
          />
        </FormGroup>
        <FormGroup>
          <Label text-color='inverse' required>Last Name</Label>
          <ValidatedInput
            inverse
            type="text"
            name="lastName"
            placeholder="Type last name"
            value={formData.lastName}
            onChange={onChange}
            validators={['required']}
            errorMessages={[ERROR_MESSAGES.required]}
          />
        </FormGroup>
        <FormGroup>
          <Label text-color='inverse' required>Email Address</Label>
          <ValidatedInput
            inverse
            type="email"
            placeholder="Type email address"
            name="email"
            value={formData.email}
            onChange={onChange}
            validators={['required', 'isEmail']}
            errorMessages={[ERROR_MESSAGES.required, ERROR_MESSAGES.email]}
          />
        </FormGroup>

        <ContentButton type="submit" disabled={!this.isValid() || isProcessing}>
          Start my journey
        </ContentButton>
      </ContentForm>
    );
  }
}

const CustomPageInfo = styled(PageInfo)`
  background: ${(p) => p.theme.colors.primary};
  position: relative;

  svg.burst {
    path {
      fill: ${(p) => p.theme.colors.white};
    }
  }

  ${(p) => p.theme.min('md')`
    margin-bottom: 50vh;

    svg.burst {
      top: 70%;
      left: 15%;
    }
  `}
`;

const CustomInfoHeader = styled(InfoHeader)`
  color: ${(p) => p.theme.colors.white};
  font-size: 3.6em;
  ${(p) => p.theme.max('md')`
    font-size: 2.5em;
  `}
`;

const CustomInfoImage = styled(InfoImage)`
  ${(p) => p.theme.min('md')`
    position: absolute;
    bottom: -10%;
    left: 50%;
    transform: translate(-50%, 50%);
  `}
`;

const CustomPageContent = styled(PageContent)`
  background: ${(p) => p.theme.colors.primary};
  ${(p) => p.theme.min('md')`
    margin-bottom: 50vh;
  `}
`;

export default SignUpPage;
